import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Touchable, PrimaryButton, SoftButton, IconNavButton } from './touchable';
import { getFullState, updateSession, savePattern } from '../storage/persistence';
import { deriveColor } from '../logic/patternColor';
import { generateDsl } from '../logic/dsl-generator';
import { PatternParamsCard } from './PatternParamsCard';
import { ProjectDetailsForm } from './ProjectDetailsForm';
import { defaultsForAll } from './paramHelpers';
import type { Session } from '../types/session';
import type { Pattern } from '../types/state';
import type { IRValue } from '../types/ir';

interface NewProjectWizardProps {
  onCancel: () => void;
  onProjectCreated: (sessionId: string) => void;
  initialPattern?: Pattern;
}

export const NewProjectWizard: React.FC<NewProjectWizardProps> = ({
  onCancel,
  onProjectCreated,
  initialPattern,
}) => {
  const { t } = useTranslation('session');
  const [patterns, setPatterns] = useState<Record<string, Pattern>>({});
  const [loading, setLoading] = useState(!initialPattern);
  const [selected, setSelected] = useState<Pattern | null>(initialPattern ?? null);
  const [details, setDetails] = useState<{
    title: string;
    metadata: Record<string, string>;
    color: string;
  }>({
    title: initialPattern?.title ?? '',
    metadata: initialPattern?.structure?.metadata ? { ...initialPattern.structure.metadata } : {},
    color: '',
  });
  const [detailsValid, setDetailsValid] = useState(true);
  const [paramValues, setParamValues] = useState<Record<string, IRValue>>(
    initialPattern ? defaultsForAll(initialPattern.structure?.params ?? []) : {},
  );
  const [paramsValid, setParamsValid] = useState(true);

  useEffect(() => {
    if (initialPattern) return;
    async function loadPatterns() {
      const state = await getFullState();
      setPatterns(state.patterns);
      setLoading(false);
    }
    loadPatterns();
  }, [initialPattern]);

  const handleStart = async () => {
    if (!selected || !detailsValid || !paramsValid) return;
    const sessionId = crypto.randomUUID();
    const now = new Date().toISOString();
    const params = selected.structure?.params ?? [];
    let compiledDsl: string | undefined;
    if (selected.js_source) {
      const defaults = defaultsForAll(selected.structure?.params ?? []);
      const isNonDefault = Object.keys(paramValues).some((k) => paramValues[k] !== defaults[k]);
      if (isNonDefault) {
        try {
          // Dynamic import keeps the JS pattern compiler (~3 KB gz) out of
          // the eager bundle. Only loaded when a user actually starts a
          // project from a JS-authored pattern with non-default params.
          const { executeJSPattern } = await import('../logic/js-authoring');
          const recompiledIR = await executeJSPattern(selected.js_source, paramValues);
          compiledDsl = generateDsl(recompiledIR);
        } catch (err) {
          alert(
            t(
              'newProject.errors.compileFailed',
              'Failed to compile pattern with chosen parameters: {{message}}',
              {
                message: err instanceof Error ? err.message : String(err),
              },
            ),
          );
          return;
        }
      }
    }
    const newSession: Session = {
      id: sessionId,
      patternId: selected.id,
      title: details.title.trim(),
      status: 'idle',
      startedAt: now,
      updatedAt: now,
      snapshot: { lastEventIndex: -1, currentRow: 0, totalTimeMs: 0 },
      history: [],
      ...(params.length > 0 ? { paramValues } : {}),
      ...(compiledDsl ? { compiledDsl } : {}),
      ...(Object.keys(details.metadata).length > 0 ? { metadata: details.metadata } : {}),
    };
    // Only persist when the user picked a color that differs from the pattern's
    // effective color (explicit `selected.color` if set, else the derived default).
    // Without this guard, every first project start would write the derived color
    // back to the pattern even when the user never touched the picker.
    const effectiveExisting = selected.color ?? deriveColor(selected.id);
    if (details.color && details.color !== effectiveExisting) {
      await savePattern({ ...selected, color: details.color });
    }
    await updateSession(newSession);
    onProjectCreated(sessionId);
  };

  const patternList = Object.values(patterns).filter((p) => !p.builtin);

  return (
    <div className="fixed inset-0 bg-base z-50 flex flex-col transition-colors duration-300">
      <header className="p-6 bg-card border-b border-line sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <IconNavButton
            onClick={selected ? (initialPattern ? onCancel : () => setSelected(null)) : onCancel}
            aria-label={t('common:actions.back', 'Back')}
            className="-ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </IconNavButton>
          <h2 className="text-2xl font-black font-display text-ink tracking-tight">
            {selected
              ? t('newProject.nameYourProject', 'Name your project')
              : t('newProject.selectPattern', 'Select Pattern')}
          </h2>
        </div>
      </header>

      {!selected ? (
        <div className="flex-grow overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-ink-faint py-10">
              {t('newProject.loadingLibrary', 'Loading library...')}
            </div>
          ) : patternList.length > 0 ? (
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {patternList.map((pattern) => (
                <Touchable
                  haptic="softTap"
                  key={pattern.id}
                  onClick={() => {
                    setSelected(pattern);
                    setDetails({
                      title: pattern.title,
                      metadata: pattern.structure?.metadata
                        ? { ...pattern.structure.metadata }
                        : {},
                      color: '',
                    });
                    setDetailsValid(true);
                    setParamValues(defaultsForAll(pattern.structure?.params ?? []));
                    setParamsValid(true);
                  }}
                  className="w-full text-left bg-card p-6 rounded-3xl border-2 border-transparent hover:border-line active:border-primary active:bg-primary-soft transition-all"
                >
                  <h3 className="font-bold text-ink text-lg mb-1">{pattern.title}</h3>
                </Touchable>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-ink-faint mb-6">
                {t('newProject.libraryEmpty', 'Your library is empty.')}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-grow overflow-y-auto p-6 max-w-4xl mx-auto w-full">
          <ProjectDetailsForm
            title={details.title}
            metadata={initialPattern?.structure?.metadata ?? selected.structure?.metadata}
            patternId={selected.id}
            initialColor={selected.color}
            onChange={setDetails}
            onValidityChange={setDetailsValid}
          />
          <p className="text-xs text-ink-faint mt-2">
            {t('newProject.patternLabel', 'Pattern: {{title}}', { title: selected.title })}
          </p>
          {selected.structure && selected.structure.params.length > 0 && (
            <div className="mt-4">
              <PatternParamsCard
                params={selected.structure.params}
                values={paramValues}
                onChange={setParamValues}
                onValidityChange={setParamsValid}
              />
            </div>
          )}
        </div>
      )}

      <div className="p-6 border-t border-line">
        {selected ? (
          <PrimaryButton
            onClick={handleStart}
            disabled={!detailsValid || !paramsValid}
            className="py-4 rounded-2xl"
          >
            {t('newProject.actions.knit', 'Knit')}
          </PrimaryButton>
        ) : (
          <SoftButton onClick={onCancel} className="text-ink-faint bg-transparent py-4">
            {t('common:actions.cancel', 'Cancel')}
          </SoftButton>
        )}
      </div>
    </div>
  );
};
